import React, { useEffect, useMemo, useState } from "react";

import { Link, useLocation } from "react-router-dom";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const QUICK_MENU = [
  {
    title: "Kurumsal",
    link: "/hakkimizda",
  },
  {
    title: "Kalite",
    link: "/portfolio-style-4",
  },
  {
    title: "İnsan Kaynakları",
    link: "/testimonial-style-1",
  },
  {
    title: "İletişim",
    link: "/contactus",
  },
];

const createSlug = (text = "") => {
  return String(text)
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};
/*
 * Veritabanından gelen logo değerini
 * kullanılabilir URL biçimine çevirir.
 */
const resolveAssetUrl = (value) => {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return "";
  }

  if (cleanValue.startsWith("http://") || cleanValue.startsWith("https://")) {
    return cleanValue;
  }

  if (cleanValue.startsWith("/cdn/")) {
    return `${API_BASE_URL}${cleanValue}`;
  }

  if (cleanValue.startsWith("cdn/")) {
    return `${API_BASE_URL}/${cleanValue}`;
  }

  return `${API_BASE_URL}/cdn/${cleanValue.replace(/^\/+/, "")}`;
};

const splitPhoneNumbers = (value) => {
  return String(value || "")
    .split(/[\n,;|]+/)
    .map((phone) => phone.trim())
    .filter(Boolean);
};

const extractEmailAddresses = (value) => {
  return (
    String(value || "").match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || []
  );
};

const createTelephoneLink = (phone) => {
  const cleanPhone = String(phone || "").replace(/[^\d+]/g, "");

  return `tel:${cleanPhone}`;
};

function FooterOne() {
  const location = useLocation();
  const [productCategories, setProductCategories] = useState([]);
  const [footerCompanyLogo, setFooterCompanyLogo] = useState("");
  const [contracts, setContracts] = useState([]);

  const [generalSettings, setGeneralSettings] = useState({
    title: "",
    title_suffix: "",
    logo_footer: "",
    footer_text: "",
    description: "",
    copyright: "",
    copyright_text: "",
    footer_company_logo: "",
  });

  const [contacts, setContacts] = useState([]);

  const [loading, setLoading] = useState(true);

  const currentLang = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    return searchParams.get("lang") || "tr";
  }, [location.search]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchFooterData = async () => {
      try {
        setLoading(true);

        const [
          generalResponse,
          contactResponse,
          productCategoryResponse,
          contractResponse,
          apiSettingsResponse,
        ] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/general?lang=${encodeURIComponent(currentLang)}`,
            {
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),

          fetch(
            `${API_BASE_URL}/api/contact/contact-info?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),

          fetch(
            `${API_BASE_URL}/api/blogs/products/categories?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),

          // SÖZLEŞMELER
          fetch(`${API_BASE_URL}/api/contract`, {
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }),

          // API SETTINGS
          fetch(`${API_BASE_URL}/api/settings`, {
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }),
        ]);
        const generalData = await generalResponse.json().catch(() => ({}));
        const contactData = await contactResponse.json().catch(() => []);
        const contractData = await contractResponse.json().catch(() => []);
        const productCategoryData = await productCategoryResponse
          .json()
          .catch(() => []);

        const apiSettingsData = await apiSettingsResponse
          .json()
          .catch(() => ({}));

        if (apiSettingsResponse.ok) {
          setFooterCompanyLogo(
            resolveAssetUrl(apiSettingsData.footer_company_logo),
          );
        } else {
          setFooterCompanyLogo("");
        }
        if (!generalResponse.ok) {
          throw new Error(generalData.message || "Genel ayarlar alınamadı.");
        }

        if (!contactResponse.ok) {
          throw new Error(
            contactData.message || "İletişim bilgileri alınamadı.",
          );
        }
        if (contractResponse.ok) {
          setContracts(Array.isArray(contractData) ? contractData : []);
        } else {
          setContracts([]);
        }

        setGeneralSettings(generalData || {});

        setContacts(
          Array.isArray(contactData)
            ? contactData
            : contactData
              ? [contactData]
              : [],
        );
        if (productCategoryResponse.ok) {
          setProductCategories(
            Array.isArray(productCategoryData) ? productCategoryData : [],
          );
        } else {
          console.error(
            "FOOTER_PRODUCT_CATEGORIES_ERROR:",
            productCategoryData,
          );

          setProductCategories([]);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("FOOTER_DATA_ERROR:", error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchFooterData();

    return () => {
      controller.abort();
    };
  }, [currentLang]);

  const footerLogo =
    resolveAssetUrl(generalSettings.logo_footer) ||
    "/assets/images/logo/logo-2.png";

  const footerText =
    generalSettings.footer_text ||
    generalSettings.description ||
    "Demirsan Soğuk Çekme, yüksek ve düşük karbonlu çelik tel üretiminde uzun yıllara dayanan tecrübesiyle hizmet vermektedir.";

  const currentYear = new Date().getFullYear();

  const companyTitle = String(
    generalSettings.title ||
      generalSettings.title_suffix ||
      "Demirsan Soğuk Çekme Sanayi ve Ticaret A.Ş.",
  ).trim();

  const rightsText = String(
    generalSettings.copyright_text ||
      generalSettings.copyright ||
      "Tüm hakları saklıdır.",
  )
    .replace(/^©\s*/i, "")
    .replace(/^\d{4}\s*/i, "")
    .trim();

  const copyrightText = `© ${currentYear} ${companyTitle} ${rightsText}`;
  /*
   * Bütün iletişim kayıtlarındaki
   * telefonları bir araya getirir.
   */
  const phoneItems = useMemo(() => {
    // Aynı label altındaki numaraları grupla
    const grouped = [];
    const seenLabels = new Map();
    const seenPhones = new Set();

    contacts.forEach((contact) => {
      const phones = splitPhoneNumbers(contact.phone).filter((p) => {
        if (seenPhones.has(p)) return false;
        seenPhones.add(p);
        return true;
      });
      if (!phones.length) return;

      const label = contact.title || contact.unvan || "Telefon";
      if (seenLabels.has(label)) {
        seenLabels.get(label).phones.push(...phones);
      } else {
        const entry = { label, phones };
        seenLabels.set(label, entry);
        grouped.push(entry);
      }
    });

    return grouped;
  }, [contacts]);

  /*
   * Bütün e-postaları tek listede toplar.
   */
  const emailAddresses = useMemo(() => {
    const emails = contacts.flatMap((contact) =>
      extractEmailAddresses(contact.email),
    );

    return Array.from(new Set(emails));
  }, [contacts]);

  const productCategoryColumns = useMemo(() => {
    const columns = [];

    for (let i = 0; i < productCategories.length; i += 5) {
      columns.push(productCategories.slice(i, i + 5));
    }

    return columns;
  }, [productCategories]);

  return (
    <footer className="demirsan-footer" id="f-contact">
      <style>
        {`
          .demirsan-footer {
            padding-top: 65px;
            background: #0d121c;
            color: #ffffff;
          }

.demirsan-footer-main {
  width: calc(100% - 100px) !important;
  max-width: 1600px !important;

  margin-left: auto !important;
  margin-right: auto !important;

  /* Footer içeriğini biraz sağa kaydır */
  padding-left: 120px !important;
  padding-right: 0 !important;

  padding-bottom: 55px;

  box-sizing: border-box;
}
          .demirsan-footer-title {
            margin: 0 0 25px;
            color: #df0a0a;
            font-size: 19px;
            font-weight: 700;
            line-height: 1.3;
          }

          .demirsan-footer-logo {
            display: inline-block;
            margin-bottom: 22px;
          }

.demirsan-footer-logo img {
  display: block;
  width: auto;
  max-width: 290px;
  height: 78px;
  object-fit: contain;
}

.demirsan-footer-description {
  width: 100%;
  max-width: 420px;

  margin: 0;

  color: rgba(255, 255, 255, 0.72);

  font-size: 14px;
  line-height: 1.8;

  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

          .demirsan-footer-menu {
            margin: 0;
            padding: 0;
            list-style: none;
          }

          .demirsan-footer-menu li {
            margin-bottom: 11px;
          }

          .demirsan-footer-menu li:last-child {
            margin-bottom: 0;
          }

          .demirsan-footer-menu a {
            display: inline-flex;
            align-items: center;
            gap: 8px;

            color: rgba(255, 255, 255, 0.8);

            font-size: 14px;
            font-weight: 500;
            line-height: 1.5;
            text-decoration: none;

            transition:
              color 0.2s ease,
              transform 0.2s ease;
          }

          .demirsan-footer-menu a i {
            color: #df0a0a;
            font-size: 11px;
          }

          .demirsan-footer-menu a:hover {
            color: #ffffff;
            transform: translateX(4px);
          }

          .demirsan-footer-contact {
            margin: 0;
            padding: 0;
            list-style: none;
          }

          .demirsan-footer-contact li {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            margin-bottom: 17px;
          }

          .demirsan-footer-contact li:last-child {
            margin-bottom: 0;
          }

          .demirsan-footer-contact-icon {
            display: flex;
            align-items: center;
            justify-content: center;

            flex: 0 0 39px;

            width: 39px;
            height: 39px;

            border-radius: 50%;

            background:
              rgba(223, 10, 10, 0.14);

            color: #df0a0a;
            font-size: 15px;
          }

          .demirsan-footer-contact-content {
            min-width: 0;
            padding-top: 1px;
          }

          .demirsan-footer-contact-label {
            display: block;
            margin-bottom: 3px;

            color:
              rgba(255, 255, 255, 0.55);

            font-size: 12px;
            line-height: 1.3;
          }

          .demirsan-footer-contact-content a {
            display: block;
            color:
              rgba(255, 255, 255, 0.85);

            font-size: 14px;
            line-height: 1.65;
            text-decoration: none;
            overflow-wrap: anywhere;

            transition: color 0.2s ease;
          }

          .demirsan-footer-contact-content a:hover {
            color: #df0a0a;
          }

          .demirsan-footer-loading {
            color:
              rgba(255, 255, 255, 0.55);

            font-size: 13px;
          }

          .demirsan-footer-empty {
            margin: 0;
            color:
              rgba(255, 255, 255, 0.55);

            font-size: 13px;
            line-height: 1.6;
          }

          .demirsan-footer-copyright {
            padding: 20px 0;

            border-top:
              1px solid
              rgba(255, 255, 255, 0.1);
          }

          .demirsan-footer-copyright p {
            margin: 0;

            color:
              rgba(255, 255, 255, 0.65);

            font-size: 14px;
            line-height: 1.6;
            text-align: center;
          }

          @media (max-width: 1199.98px) {
            .demirsan-footer-column {
              margin-bottom: 32px;
            }
          }

          @media (max-width: 991.98px) {
            .demirsan-footer {
              padding-top: 50px;
            }
          }

          @media (max-width: 767.98px) {
            .demirsan-footer {
              padding-top: 40px;
            }

            .demirsan-footer-main {
              padding-bottom: 10px;
            }

            .demirsan-footer-logo img {
              height: 55px;
              max-width: 200px;
            }

            .demirsan-footer-title {
              margin-bottom: 18px;
            }
          }
            /* =========================================================
   DİNAMİK ÜRÜN KATEGORİLERİ
========================================================= */

.demirsan-footer-product-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 28px;
  row-gap: 18px;

  width: 100%;
}

.demirsan-footer-product-column {
  margin: 0;
  padding: 0;

  list-style: none;
}

.demirsan-footer-product-column li {
  margin-bottom: 11px;
}

.demirsan-footer-product-column li:last-child {
  margin-bottom: 0;
}

.demirsan-footer-product-column a {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  color: rgba(255, 255, 255, 0.8);

  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  text-decoration: none;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.demirsan-footer-product-column a i {
  flex-shrink: 0;

  margin-top: 5px;

  color: #df0a0a;
  font-size: 11px;
}

.demirsan-footer-product-column a:hover {
  color: #ffffff;
  transform: translateX(4px);
}

.demirsan-footer-products-empty {
  margin: 0;

  color: rgba(255, 255, 255, 0.55);

  font-size: 13px;
  line-height: 1.6;
}

@media (max-width: 767.98px) {
  .demirsan-footer-product-columns {
    grid-template-columns: 1fr;
    row-gap: 11px;
  }
}
  /* =========================================================
   FOOTER ÜRÜNLER ALANI
========================================================= */

.demirsan-footer-products-column {
  width: 100%;
}

/*
 * Ürünler alanını kendi kolonunun ortasına alır.
 * Böylece 2-3 kategori olduğunda sola yapışmaz.
 */
.demirsan-footer-products-inner {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}


/* Başlık listeyle aynı hizada */
.demirsan-footer-products-inner
.demirsan-footer-title {
  text-align: left;
}


/* =========================================================
   ÜRÜN KATEGORİLERİ
========================================================= */

.demirsan-footer-product-columns {
  display: grid;
  align-items: start;

  width: fit-content;
  max-width: 100%;

  column-gap: 42px;
  row-gap: 0;
}


/*
 * 1 - 5 kategori:
 *
 * Ürünler
 * → kategori 1
 * → kategori 2
 * → kategori 3
 */
.demirsan-footer-product-columns.single-column {
  grid-template-columns: max-content;
}


/*
 * 6 - 10 kategori:
 *
 * → 1       → 6
 * → 2       → 7
 * → 3       → 8
 * → 4       → 9
 * → 5       → 10
 */
.demirsan-footer-product-columns.multi-column {
  grid-template-columns:
    repeat(2, max-content);
}


.demirsan-footer-product-column {
  margin: 0;
  padding: 0;

  list-style: none;
}


.demirsan-footer-product-column li {
  margin-bottom: 11px;
}


.demirsan-footer-product-column li:last-child {
  margin-bottom: 0;
}


.demirsan-footer-product-column a {
  display: flex;
  align-items: center;
  gap: 8px;

  color: rgba(255, 255, 255, 0.8);

  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;

  white-space: nowrap;

  text-decoration: none;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}


.demirsan-footer-product-column a i {
  flex-shrink: 0;

  color: #df0a0a;

  font-size: 11px;
}


.demirsan-footer-product-column a:hover {
  color: #ffffff;

  transform: translateX(4px);
}


/* =========================================================
   TABLET / MOBİL
========================================================= */

@media (max-width: 767.98px) {

  .demirsan-footer-products-inner {
    width: 100%;
    margin: 0;
  }

  .demirsan-footer-product-columns,
  .demirsan-footer-product-columns.single-column,
  .demirsan-footer-product-columns.multi-column {
    grid-template-columns: 1fr;

    width: 100%;

    row-gap: 11px;
  }

  .demirsan-footer-product-column a {
    white-space: normal;
  }
}
  .demirsan-footer-copyright {
  padding: 16px 0;

  border-top:
    1px solid
    rgba(255, 255, 255, 0.1);
}

.demirsan-footer-copyright-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  min-height: 42px;
}

.demirsan-footer-copyright p {
  margin: 0;

  color: rgba(255, 255, 255, 0.65);

  font-size: 14px;
  line-height: 1.6;

  text-align: left;
}

.demirsan-footer-company-logo {
  display: block;

  width: auto;

  /* 120px -> %40 küçültüldü */
  max-width: 72px;

  /* 32px -> yaklaşık %40 küçültüldü */
  height: 20px;

  object-fit: contain;
}

/* MOBİL */
@media (max-width: 767.98px) {
  .demirsan-footer-copyright-inner {
    flex-direction: column;
    justify-content: center;

    gap: 10px;
  }

  .demirsan-footer-copyright p {
    text-align: center;
  }

  .demirsan-footer-company-logo {
    /* 100px -> %40 küçültüldü */
    max-width: 60px;

    /* 28px -> yaklaşık %40 küçültüldü */
    height: 17px;
  }
}
  @media (min-width: 1200px) {

  /* 2.5 / 12 */
  .demirsan-footer-contract-col {
    flex: 0 0 20.833333% !important;
    width: 20.833333% !important;
    max-width: 20.833333% !important;
  }

  /* 3.5 / 12 */
  .demirsan-footer-contact-col {
    flex: 0 0 29.166667% !important;
    width: 29.166667% !important;
    max-width: 29.166667% !important;
  }

}
  @media (max-width: 1199.98px) {
  .demirsan-footer-main {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
}
        `}
      </style>

      <div className="container demirsan-footer-main">
        <div className="row g-4">
          {/* LOGO VE FOOTER METNİ */}
          <div className="col-xl-4 col-md-6 col-12">
            <div className="demirsan-footer-column">
              <Link className="demirsan-footer-logo" to={`/`}>
                <img
                  src={footerLogo}
                  alt="Demirsan Soğuk Çekme"
                  onError={(event) => {
                    event.currentTarget.src = "/assets/images/logo/logo-2.png";
                  }}
                />
              </Link>

              <p className="demirsan-footer-description">{footerText}</p>
            </div>
          </div>

          {/* HIZLI MENÜ */}
          <div className="col-xl-2 col-md-6 col-12">
            <div className="demirsan-footer-column">
              <h3 className="demirsan-footer-title">Hızlı Menü</h3>

              <ul className="demirsan-footer-menu">
                {QUICK_MENU.map((item) => (
                  <li key={item.title}>
                    <Link to={`${item.link}`}>
                      <i className="far fa-arrow-right" />

                      {item.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {/* ÜRÜN KATEGORİLERİ */}
          {/* SÖZLEŞMELER */}
          <div className="demirsan-footer-contract-col col-md-6 col-12">
            <div className="demirsan-footer-column">
              <h3 className="demirsan-footer-title">Sözleşmeler</h3>

              {contracts.length > 0 ? (
                <ul className="demirsan-footer-menu">
                  {contracts.map((contract) => {
                    const title = contract.title || `Sözleşme ${contract.id}`;

                    const slug = `${createSlug(title)}-${contract.id}`;

                    const contractUrl =
                      currentLang === "tr"
                        ? `/sozlesmeler/${slug}`
                        : `/sozlesmeler/${slug}?lang=${encodeURIComponent(
                            currentLang,
                          )}`;

                    return (
                      <li key={contract.id}>
                        <Link to={contractUrl}>
                          <i className="far fa-arrow-right" />

                          <span>{title}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          </div>

          {/* İLETİŞİM BİLGİLERİ */}
          <div className="col-xl-2 col-md-6 col-12">
            <div className="demirsan-footer-column">
              <h3 className="demirsan-footer-title">İletişim Bilgileri</h3>

              {loading ? (
                <span className="demirsan-footer-loading">
                  İletişim bilgileri yükleniyor...
                </span>
              ) : (
                <ul className="demirsan-footer-contact">
                  {phoneItems.map((item) => (
                    <li key={item.label}>
                      <div className="demirsan-footer-contact-icon">
                        <i className="fas fa-phone-alt" />
                      </div>

                      <div className="demirsan-footer-contact-content">
                        <span className="demirsan-footer-contact-label">
                          {item.label}
                        </span>

                        {item.phones.map((phone) => (
                          <a key={phone} href={createTelephoneLink(phone)}>
                            {phone}
                          </a>
                        ))}
                      </div>
                    </li>
                  ))}

                  {emailAddresses.length > 0 && (
                    <li>
                      <div className="demirsan-footer-contact-icon">
                        <i className="fas fa-envelope" />
                      </div>

                      <div className="demirsan-footer-contact-content">
                        <span className="demirsan-footer-contact-label">
                          E-posta
                        </span>

                        {emailAddresses.map((email) => (
                          <a key={email} href={`mailto:${email}`}>
                            {email}
                          </a>
                        ))}
                      </div>
                    </li>
                  )}

                  {phoneItems.length === 0 && emailAddresses.length === 0 && (
                    <li>
                      <p className="demirsan-footer-empty">
                        İletişim bilgisi bulunamadı.
                      </p>
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="demirsan-footer-copyright">
        <div className="container">
          <div className="demirsan-footer-copyright-inner">
            <p>{copyrightText}</p>

            {footerCompanyLogo && (
              <a
                href="https://www.kimbilisim.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Kim Bilişim"
                title="Kim Bilişim"
              >
                <img
                  className="demirsan-footer-company-logo"
                  src={footerCompanyLogo}
                  alt="Kim Bilişim"
                />
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FooterOne;
